// app/src/Chat.jsx — async discussions (channels, groups, DMs).
//
// Two views: a thread list and a thread (messages). Messages are translated to
// the reader's language on a paid workplace; free shows originals. Opening a
// thread advances the read marker (clears the unread dot).

// Little globe glyph used on translation captions/banners.
function Globe({ size = 12 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none' }}>
      <circle cx="12" cy="12" r="9" /><path d="M3 12h18" /><path d="M12 3c2.5 2.6 2.5 15.4 0 18M12 3c-2.5 2.6-2.5 15.4 0 18" />
    </svg>
  );
}

function ChatScreen() {
  const nav = useNav();
  const wid = window.crew && window.crew.state.activeWorkplaceId;
  const me = (window.crew && window.crew.state.me) || { workplaces: [] };
  const myWp = (me.workplaces || []).find((w) => w.workplaceId === wid) || {};
  const myLang = myWp.preferredLang || 'en';
  const myName = myWp.displayName || t('common.member', 'Member');

  const [threads, setThreads] = React.useState(null);
  const [members, setMembers] = React.useState([]);
  const [active, setActive] = React.useState(null); // thread object
  const [messages, setMessages] = React.useState(null);
  const [draft, setDraft] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [composing, setComposing] = React.useState(false);
  const [showOrig, setShowOrig] = React.useState({}); // messageId → showing original?
  const toggleOrig = (id) => setShowOrig((s) => ({ ...s, [id]: !s[id] }));
  const [pending, setPending] = React.useState([]); // [{ file, url, contentType, w, h }] — up to 3 photos
  const fileRef = React.useRef(null);

  // Pick up to 3 photos; load each to grab its natural dimensions for the bubble.
  const pickPhotos = async (e) => {
    const files = Array.from(e.target.files || []).filter((f) => f.type.startsWith('image/'));
    e.target.value = '';
    if (!files.length) return;
    const take = files.slice(0, Math.max(0, 3 - pending.length));
    const loaded = await Promise.all(take.map((file) => new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => resolve({ file, url, contentType: file.type, w: img.naturalWidth, h: img.naturalHeight });
      img.onerror = () => resolve({ file, url, contentType: file.type, w: null, h: null });
      img.src = url;
    })));
    setPending((p) => [...p, ...loaded].slice(0, 3));
  };
  const removePending = (i) => setPending((p) => { const x = p[i]; if (x) URL.revokeObjectURL(x.url); return p.filter((_, idx) => idx !== i); });

  const loadThreads = React.useCallback(async () => {
    if (!wid) return;
    try { const r = await window.crew.api(`/workplaces/${wid}/threads`, { workplaceId: wid }); setThreads((r && r.threads) || []); }
    catch (e) { console.warn('[chat] threads', e && e.message); setThreads([]); }
  }, [wid]);

  React.useEffect(() => { loadThreads(); }, [loadThreads]);

  // Member directory → resolve DM/group titles to the other participant's name, so
  // you always know WHO you're talking to (not just "Direct message").
  React.useEffect(() => {
    if (!wid) return;
    window.crew.api(`/workplaces/${wid}/members`, { workplaceId: wid })
      .then((r) => setMembers((r && r.members) || [])).catch(() => {});
  }, [wid]);
  const meId = window.crew.state.me.profile && window.crew.state.me.profile.userId;
  const nameOf = (id) => { const m = members.find((x) => x.userId === id); return (m && m.displayName) || null; };
  const avatarOf = (id) => { const m = members.find((x) => x.userId === id); return (m && m.avatarUrl) || null; };
  const langOf = (id) => { const m = members.find((x) => x.userId === id); return (m && m.preferredLang) || null; };
  const dmPartnerId = (tdef) => (tdef.type === 'dm' ? (tdef.memberIds || []).find((id) => id !== meId) : null);
  const titleOf = (tdef) => threadTitle(tdef, nameOf, meId);

  const openThread = async (tdef) => {
    setActive(tdef); setMessages(null);
    try {
      const r = await window.crew.api(`/workplaces/${wid}/threads/${tdef.threadId}/messages?lang=${encodeURIComponent(myLang)}`, { workplaceId: wid });
      setMessages((r && r.messages) || []);
      window.crew.api(`/workplaces/${wid}/threads/${tdef.threadId}/read`, { method: 'POST', workplaceId: wid, body: {} }).catch(() => {});
    } catch (e) { console.warn('[chat] messages', e && e.message); setMessages([]); }
  };

  const send = async () => {
    const text = draft.trim();
    if ((!text && pending.length === 0) || busy || !active) return;
    setBusy(true);
    try {
      // Upload photos straight to S3 via presigned PUTs, then send the keys.
      let attachments = [];
      if (pending.length) {
        const pres = await window.crew.api(`/workplaces/${wid}/threads/${active.threadId}/attachments/presign`, {
          method: 'POST', workplaceId: wid, body: { items: pending.map((p) => ({ contentType: p.contentType })) },
        });
        const slots = (pres && pres.attachments) || [];
        await Promise.all(slots.map((slot, i) => fetch(slot.putUrl, { method: 'PUT', headers: { 'content-type': slot.contentType }, body: pending[i].file })));
        attachments = slots.map((slot, i) => ({ key: slot.key, contentType: slot.contentType, w: pending[i].w, h: pending[i].h }));
      }
      await window.crew.api(`/workplaces/${wid}/threads/${active.threadId}/messages`, { method: 'POST', workplaceId: wid, body: { body: text, authorName: myName, sourceLang: myLang, attachments } });
      setDraft('');
      pending.forEach((p) => URL.revokeObjectURL(p.url));
      setPending([]);
      const r = await window.crew.api(`/workplaces/${wid}/threads/${active.threadId}/messages?lang=${encodeURIComponent(myLang)}`, { workplaceId: wid });
      setMessages((r && r.messages) || []);
    } catch (e) { console.warn('[chat] send', e && e.message); } finally { setBusy(false); }
  };

  // ── Thread view ──
  if (active) {
    const partnerId = dmPartnerId(active);
    const subtitle = active.type === 'dm'
      ? (langOf(partnerId) ? t('chat.readsLang', 'reads {lang}', { lang: window.langName(langOf(partnerId)) }) : '')
      : t('chat.memberCount', '{n} members', { n: active.type === 'channel' ? members.length : (active.memberIds || []).length });
    return (
      <KShell tab="chat">
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '10px 12px 11px', borderBottom: '1px solid var(--line-on)', flex: 'none' }}>
          <button onClick={() => { setActive(null); loadThreads(); }} style={{ border: 'none', background: 'none', color: 'var(--on)', fontSize: 22, cursor: 'pointer', lineHeight: 1, flex: 'none', padding: 0 }}>‹</button>
          {partnerId
            ? <Avatar name={nameOf(partnerId)} url={avatarOf(partnerId)} size={34} />
            : (
              <div style={{ width: 34, height: 34, borderRadius: 11, background: 'var(--accent-soft)', color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="9" cy="8" r="3.2" /><path d="M2.5 19c0-3 2.9-5 6.5-5s6.5 2 6.5 5" /><path d="M16 6.2A3.2 3.2 0 0 1 17 12.3M21.5 19c0-2.4-1.6-4.2-4-4.8" /></svg>
              </div>
            )}
          <div style={{ flex: 1, minWidth: 0, lineHeight: 1.2 }}>
            <div style={{ fontFamily: 'var(--font-head)', fontWeight: 700, fontSize: 15.5, color: 'var(--on)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{titleOf(active)}</div>
            {subtitle && <div style={{ fontSize: 10.5, color: 'var(--on-soft)' }}>{subtitle}</div>}
          </div>
          <button onClick={() => nav.navigate('/you')} title={t('chat.yourLanguage', 'Your language')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'var(--primary-soft)', color: 'var(--primary)', fontSize: 10, fontWeight: 800, padding: '5px 9px', borderRadius: 999, border: 'none', cursor: 'pointer', flex: 'none' }}>
            <Globe size={12} />{String(myLang).toUpperCase()}
          </button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {messages == null && <div style={{ color: 'var(--on-soft)' }}>…</div>}
          {messages != null && messages.length === 0 && <div style={{ color: 'var(--on-soft)', textAlign: 'center', marginTop: 20 }}>{t('chat.noMessages', 'No messages yet.')}</div>}
          {messages != null && messages.length > 0 && (
            <div style={{ alignSelf: 'center', display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--accent-soft)', color: 'var(--accent)', fontSize: 10, fontWeight: 700, padding: '5px 11px', borderRadius: 999 }}>
              <Globe size={12} />{t('chat.shownIn', 'Messages shown in {lang}', { lang: window.langName(myLang) })}
            </div>
          )}
          {(messages || []).map((m) => {
            const mine = m.authorId === meId;
            const showing = !!showOrig[m.messageId];
            const text = showing && m.originalBody ? m.originalBody : m.body;
            return (
              <div key={m.messageId} style={{ display: 'flex', gap: 8, alignItems: 'flex-end', alignSelf: mine ? 'flex-end' : 'flex-start', maxWidth: '88%' }}>
                {!mine && <Avatar name={m.authorName} url={avatarOf(m.authorId)} size={26} />}
                <div style={{ minWidth: 0 }}>
                  {!mine && <div style={{ fontSize: 10, color: 'var(--on-soft)', fontWeight: 600, margin: '0 0 3px 3px' }}>{m.authorName}</div>}
                  {m.attachments && m.attachments.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: m.body ? 6 : 0, justifyContent: mine ? 'flex-end' : 'flex-start' }}>
                      {m.attachments.map((a, i) => <PhotoTile key={i} a={a} n={m.attachments.length} />)}
                    </div>
                  )}
                  {m.body && <div style={{ background: mine ? 'var(--primary)' : 'var(--surface)', color: mine ? 'var(--primary-ink)' : 'var(--ink)', border: mine ? 'none' : '1px solid var(--line)', boxShadow: mine ? 'none' : 'var(--shadow)', borderRadius: mine ? '16px 4px 16px 16px' : '4px 16px 16px 16px', padding: '9px 12px', fontSize: 13.5, lineHeight: 1.45, whiteSpace: 'pre-wrap' }}>{text}</div>}
                  {m.translatedFrom && (
                    <div onClick={m.originalBody ? () => toggleOrig(m.messageId) : undefined}
                      style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 9.5, color: 'var(--on-soft)', margin: '4px 0 0 3px', cursor: m.originalBody ? 'pointer' : 'default', justifyContent: mine ? 'flex-end' : 'flex-start' }}>
                      <Globe size={11} />{t('chat.translatedFromName', 'Translated from {lang}', { lang: window.langName(m.translatedFrom) })}
                      {m.originalBody && <React.Fragment> · <span style={{ color: 'var(--primary)', fontWeight: 700 }}>{showing ? t('chat.showTranslation', 'Show translation') : t('chat.viewOriginal', 'View original')}</span></React.Fragment>}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ borderTop: '1px solid var(--line-on)' }}>
          {pending.length > 0 && (
            <div style={{ display: 'flex', gap: 8, padding: '8px 12px 0', alignItems: 'center', flexWrap: 'wrap' }}>
              {pending.map((p, i) => (
                <div key={i} style={{ position: 'relative' }}>
                  <img src={p.url} alt="" style={{ width: 46, height: 46, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--line)', display: 'block' }} />
                  <button onClick={() => removePending(i)} style={{ position: 'absolute', top: -6, right: -6, width: 18, height: 18, borderRadius: '50%', border: 'none', background: 'rgba(20,12,8,.7)', color: '#fff', fontSize: 12, lineHeight: '18px', cursor: 'pointer', padding: 0 }}>×</button>
                </div>
              ))}
              <span style={{ fontSize: 10, color: 'var(--on-soft)' }}>{pending.length}/3 · {t('chat.keptDays', 'kept 30 days')}</span>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, padding: '10px 12px', alignItems: 'center' }}>
            <input ref={fileRef} type="file" accept="image/*" multiple onChange={pickPhotos} style={{ display: 'none' }} />
            <button onClick={() => fileRef.current && fileRef.current.click()} disabled={pending.length >= 3} title={t('chat.addPhoto', 'Add photo')}
              style={{ border: '1px solid var(--line)', background: 'var(--surface)', borderRadius: 999, width: 38, height: 38, flex: 'none', fontSize: 17, cursor: pending.length >= 3 ? 'default' : 'pointer', color: pending.length >= 3 ? 'var(--sub)' : 'var(--primary)', opacity: pending.length >= 3 ? 0.5 : 1 }}>🖼</button>
            <MicButton lang={myLang} disabled={busy} onText={(text) => setDraft((d) => (d ? d + ' ' : '') + text)} size={38} />
            <input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
              placeholder={t('chat.messagePlaceholder', 'Message…')} style={{ flex: 1, padding: '10px 12px', borderRadius: 999, border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--ink)', fontSize: 14 }} />
            <button onClick={send} disabled={(!draft.trim() && pending.length === 0) || busy} style={{ border: 'none', borderRadius: 999, padding: '0 16px', background: 'var(--primary)', color: 'var(--primary-ink)', fontWeight: 700, cursor: 'pointer', opacity: (draft.trim() || pending.length) && !busy ? 1 : 0.5 }}>{t('common.send', 'Send')}</button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 9.5, color: 'var(--on-soft)', padding: '0 14px 10px' }}>
            <Globe size={11} />{t('chat.typeHint', 'You type in {lang} — everyone reads in their own language', { lang: window.langName(myLang) })}
          </div>
        </div>
      </KShell>
    );
  }

  // ── Thread list ──
  return (
    <KShell tab="chat">
      <KHeader title={t('nav.chat', 'Chat')} right={
        <button onClick={() => nav.navigate('/you')} title={t('chat.yourLanguage', 'Your language')}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'var(--primary-soft)', color: 'var(--primary)', fontSize: 10, fontWeight: 800, padding: '5px 9px', borderRadius: 999, border: 'none', cursor: 'pointer', flex: 'none' }}>
          <Globe size={12} />{String(myLang).toUpperCase()}
        </button>
      } />
      <div style={{ padding: '12px 14px' }}>
        <button onClick={() => setComposing(true)} style={{ width: '100%', padding: '11px', marginBottom: 12, border: '1px dashed var(--line)', background: 'var(--surface)', borderRadius: 'var(--radius-sm)', color: 'var(--primary)', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>+ {t('chat.new', 'New conversation')}</button>
        {threads == null && <div style={{ color: 'var(--on-soft)', padding: 12 }}>…</div>}
        {threads != null && threads.length === 0 && <div style={{ color: 'var(--on-soft)', textAlign: 'center', padding: 20 }}>{t('chat.empty', 'No conversations yet.')}</div>}
        {(threads || []).map((tdef) => (
          <div key={tdef.threadId} onClick={() => openThread(tdef)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 12, marginBottom: 8, background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 'var(--radius)', cursor: 'pointer' }}>
            {(() => {
              const pid = dmPartnerId(tdef);
              if (pid) return <Avatar name={nameOf(pid)} url={avatarOf(pid)} size={36} />;
              return <div style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--accent-soft)', color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800 }}>{tdef.type === 'channel' ? '#' : '👥'}</div>;
            })()}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)' }}>{titleOf(tdef)}</div>
              <div style={{ fontSize: 12, color: 'var(--sub)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tdef.lastMessageText || t('chat.noMessagesShort', 'No messages yet')}</div>
            </div>
            {tdef.unread && <span style={{ width: 9, height: 9, borderRadius: '50%', background: 'var(--primary)' }} />}
          </div>
        ))}
      </div>
      {composing && <NewConversation wid={wid} onClose={() => setComposing(false)} onCreated={(tdef) => { setComposing(false); loadThreads().then(() => openThread(tdef)); }} />}
    </KShell>
  );
}

// A single photo in a message bubble. Falls back to a "photo expired" tile once
// the S3 object is gone (30-day lifecycle) and the presigned url 404s.
function PhotoTile({ a, n }) {
  const [bad, setBad] = React.useState(false);
  const size = n === 1 ? 180 : (n === 2 ? 120 : 86);
  if (bad || !a.url) {
    return (
      <div style={{ width: size, height: size, borderRadius: 12, background: 'var(--surface-2, #f1eadf)', border: '1px solid var(--line)', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', color: 'var(--sub)', fontSize: 10.5, padding: 6, boxSizing: 'border-box' }}>
        {t('chat.photoExpired', 'Photo expired')}
      </div>
    );
  }
  return <img src={a.url} alt="" onError={() => setBad(true)} style={{ width: size, height: size, objectFit: 'cover', borderRadius: 12, border: '1px solid var(--line)', display: 'block' }} />;
}

// Title a thread by WHO it's with: channels by name, named groups by name, and
// DMs/unnamed groups by the other participant(s)' display names (so a DM never just
// reads "Direct message"). Falls back to the generic label until members load.
function threadTitle(tdef, nameOf, meId) {
  if (tdef.type === 'channel') return tdef.name ? `# ${tdef.name}` : t('chat.channel', 'Channel');
  if (tdef.name) return tdef.name; // explicitly named group
  const others = (tdef.memberIds || []).filter((id) => id !== meId);
  const names = others.map((id) => (nameOf ? nameOf(id) : null)).filter(Boolean);
  if (names.length) return names.join(', ');
  return tdef.type === 'dm' ? t('chat.directMessage', 'Direct message') : t('chat.group', 'Group');
}

function NewConversation({ wid, onClose, onCreated }) {
  const [kind, setKind] = React.useState('channel'); // channel | dm
  const [name, setName] = React.useState('');
  const [members, setMembers] = React.useState([]);
  const [picked, setPicked] = React.useState([]);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    window.crew.api(`/workplaces/${wid}/members`, { workplaceId: wid }).then((r) => setMembers((r && r.members) || [])).catch(() => {});
  }, [wid]);

  const create = async () => {
    setBusy(true);
    try {
      const body = kind === 'channel' ? { type: 'channel', name: name.trim() } : { type: picked.length > 1 ? 'group' : 'dm', memberIds: picked };
      const r = await window.crew.api(`/workplaces/${wid}/threads`, { method: 'POST', workplaceId: wid, body });
      onCreated({ threadId: r.threadId, type: r.type, name: r.name, memberIds: body.memberIds || null });
    } catch (e) { console.warn(e); setBusy(false); }
  };

  const meId = window.crew.state.me.profile && window.crew.state.me.profile.userId;
  const canCreate = kind === 'channel' ? !!name.trim() : picked.length > 0;

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(20,12,8,.45)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 50 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 430, background: 'var(--surface)', borderTopLeftRadius: 22, borderTopRightRadius: 22, padding: 20, maxHeight: '80%', overflowY: 'auto' }}>
        <div style={{ fontFamily: 'var(--font-head)', fontWeight: 800, fontSize: 18, marginBottom: 14, color: 'var(--ink)' }}>{t('chat.new', 'New conversation')}</div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          {['channel', 'dm'].map((k) => (
            <button key={k} onClick={() => setKind(k)} style={{ flex: 1, padding: '8px', borderRadius: 999, border: '1px solid var(--line)', cursor: 'pointer', background: kind === k ? 'var(--primary)' : 'var(--surface)', color: kind === k ? 'var(--primary-ink)' : 'var(--ink)', fontWeight: 700, fontSize: 13 }}>{k === 'channel' ? t('chat.channel', 'Channel') : t('chat.people', 'People')}</button>
          ))}
        </div>
        {kind === 'channel' ? (
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('chat.channelNamePlaceholder', 'Channel name (e.g. Back of House)')} style={{ width: '100%', padding: '11px 13px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--ink)', fontSize: 15, boxSizing: 'border-box', marginBottom: 14 }} />
        ) : (
          <div style={{ marginBottom: 14 }}>
            {members.filter((m) => m.userId !== meId).map((m) => {
              const on = picked.includes(m.userId);
              return (
                <label key={m.userId} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 4px', cursor: 'pointer', fontSize: 14, color: 'var(--ink)' }}>
                  <input type="checkbox" checked={on} onChange={() => setPicked((p) => on ? p.filter((x) => x !== m.userId) : [...p, m.userId])} />
                  {m.displayName || t('common.member', 'Member')} <span style={{ fontSize: 11, color: 'var(--sub)' }}>{m.preferredLang ? window.langName(m.preferredLang) : ''}</span>
                </label>
              );
            })}
          </div>
        )}
        <button onClick={create} disabled={!canCreate || busy} style={{ width: '100%', padding: 12, border: 'none', borderRadius: 'var(--radius-sm)', background: 'var(--primary)', color: 'var(--primary-ink)', fontWeight: 700, fontSize: 15, cursor: 'pointer', opacity: canCreate && !busy ? 1 : 0.6 }}>{busy ? '…' : t('common.create', 'Create')}</button>
        <button onClick={onClose} style={{ width: '100%', marginTop: 8, padding: 10, border: 'none', background: 'none', color: 'var(--sub)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{t('common.cancel', 'Cancel')}</button>
      </div>
    </div>
  );
}

Object.assign(window, { ChatScreen, NewConversation, PhotoTile });
